import {
  ArrowPathIcon,
  Bars3Icon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  QueueListIcon,
} from "@heroicons/react/20/solid";
import type {PropsWithChildren, ReactNode, Ref} from "react";
import {AdminPage} from "../components/AdminPage";
import {OtsBadge} from "../components/OtsBadge";
import {OtsIconActionButton} from "../components/OtsButton";
import {OtsDayStepper} from "../components/OtsDayStepper";
import {OtsThemeRoot} from "../theme/OtsTheme";

export type DispatchWorkspaceProps = PropsWithChildren<{
  date: Date;
  onDateChange: (date: Date) => void;
  dispatchCount: number;
  canceledRequestCount?: number;
  view: "LIST" | "BLOCK";
  onViewChange: (view: "LIST" | "BLOCK") => void;
  showSearch: boolean;
  onSearchClick: () => void;
  filterActive: boolean;
  onFilterClick: () => void;
  isRefreshing?: boolean;
  onRefresh: () => void;
  onNewDispatch?: () => void;
  gridControlsRef?: Ref<HTMLDivElement>;
  context?: ReactNode;
}>;

/** Dispatch's compact admin workspace: context above the canvas, centered date and icon actions. */
export function DispatchWorkspace({
  date,
  onDateChange,
  dispatchCount,
  canceledRequestCount = 0,
  view,
  onViewChange,
  showSearch,
  onSearchClick,
  filterActive,
  onFilterClick,
  isRefreshing,
  onRefresh,
  onNewDispatch,
  gridControlsRef,
  context,
  children,
}: DispatchWorkspaceProps) {
  return (
    <OtsThemeRoot className="h-full min-h-0 min-w-0">
      <AdminPage
        title="Dispatch"
        fullWidth
        layout="fill"
        density="compact"
        headerLayout="workspace"
        headerControlsPlacement="center"
        status={
          <>
            <span aria-label="Dispatch count" className="text-xs tabular-nums text-[var(--ots-text-muted)]">
              {dispatchCount.toLocaleString()}
            </span>
            {canceledRequestCount > 0 && (
              <OtsBadge tone="warning" size="compact">
                {canceledRequestCount} for canceled requests
              </OtsBadge>
            )}
          </>
        }
        headerControls={
          <div role="group" aria-label="Dispatch date navigation">
            <OtsDayStepper size="compact" value={date} onChange={onDateChange} />
          </div>
        }
        actions={
          <>
            <div role="group" aria-label="Find dispatches" className="flex items-center gap-1">
              {view === "LIST" && (
                <OtsIconActionButton
                  size="compact"
                  variant={showSearch ? "tonal" : "text"}
                  icon={<MagnifyingGlassIcon />}
                  label="Search dispatches"
                  aria-pressed={showSearch}
                  onClick={onSearchClick}
                />
              )}
              <OtsIconActionButton
                size="compact"
                variant={filterActive ? "tonal" : "text"}
                icon={<FunnelIcon />}
                label={filterActive ? "Filter dispatches (active)" : "Filter dispatches"}
                aria-pressed={filterActive}
                onClick={onFilterClick}
              />
            </div>
            <span aria-hidden="true" className="h-5 w-px shrink-0 bg-[var(--ots-outline-variant)]" />
            <div role="group" aria-label="Dispatch view" className="flex items-center gap-1">
              <OtsIconActionButton
                size="compact"
                variant={view === "LIST" ? "tonal" : "text"}
                icon={<Bars3Icon />}
                label="List view"
                aria-pressed={view === "LIST"}
                onClick={() => onViewChange("LIST")}
              />
              <OtsIconActionButton
                size="compact"
                variant={view === "BLOCK" ? "tonal" : "text"}
                icon={<QueueListIcon />}
                label="Block view"
                aria-pressed={view === "BLOCK"}
                onClick={() => onViewChange("BLOCK")}
              />
            </div>
            {view === "LIST" && (
              <>
                <span aria-hidden="true" className="h-5 w-px shrink-0 bg-[var(--ots-outline-variant)]" />
                <div ref={gridControlsRef} role="group" aria-label="Dispatch grid controls" />
              </>
            )}
            <span aria-hidden="true" className="h-5 w-px shrink-0 bg-[var(--ots-outline-variant)]" />
            <div role="group" aria-label="Dispatch actions" className="flex items-center gap-1">
              <OtsIconActionButton
                size="compact"
                variant="text"
                icon={<ArrowPathIcon className={isRefreshing ? "animate-spin" : undefined} />}
                label="Refresh dispatches"
                disabled={isRefreshing}
                onClick={onRefresh}
              />
              {onNewDispatch && (
                <OtsIconActionButton
                  size="compact"
                  variant="text"
                  icon={<PlusIcon />}
                  label="New dispatch"
                  onClick={onNewDispatch}
                />
              )}
            </div>
          </>
        }
      >
        {context != null && <div className="mt-2 shrink-0">{context}</div>}
        <section
          aria-label="Dispatch data grid"
          aria-busy={isRefreshing || undefined}
          className="relative mt-2 !flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]"
        >
          {children}
        </section>
      </AdminPage>
    </OtsThemeRoot>
  );
}
